import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Body2 } from '@ui/Typography'
import { Link } from '@ui/components/Link'
import { FileIcon } from '@ui/icons/File.icon'

import {
  LegalDocumentDto,
  isLegalDocumentWidgetCheckbox,
  isLegalDocumentWidgetReadAndAgree,
  isLegalDocumentsWidgetCheckbox,
} from '@model/CompanyLegalDocumentDto'

import { Box } from '@shared/components/Box'

import { DocumentFieldItem as DocumentCheckBoxFieldItem } from '../../../Components/DocumentCheckBoxField'
import {
  DocumentFieldItem,
  DocumentFieldItem as DocumentReadAndAgreeFieldItem,
} from '../../../Components/DocumentReadAndAgreeField'

import styles from './DocumentItemFactory.module.scss'

export const DocumentItemFactory: React.FC<
  DocumentCheckBoxFieldItem | DocumentReadAndAgreeFieldItem
> = (props) => {
  const { documents = [], component } = props

  if (isLegalDocumentWidgetReadAndAgree(documents)) {
    const { title, isAccepted, onClick } = props as DocumentReadAndAgreeFieldItem

    return (
      <div className={classNames(styles.read, styles.mb)}>
        <ReadAndAgree
          documents={documents}
          title={title}
          isAccepted={isAccepted}
          onClick={onClick}
        />
      </div>
    )
  }

  if (isLegalDocumentsWidgetCheckbox(documents)) {
    return (
      <div className={classNames(styles.read, styles.mb)}>
        <DocumentCheckBoxGroup documents={documents} />
      </div>
    )
  }

  if (isLegalDocumentWidgetCheckbox(documents)) {
    return (
      <div className={classNames(styles.read, styles.mb)}>
        <DocumentCheckBox documents={documents} />
      </div>
    )
  }

  return <React.Fragment>{component}</React.Fragment>
}

export const ReadAndAgree: React.FC<Omit<DocumentFieldItem, 'component'>> = (props) => {
  const { t } = useTranslation()

  const { title, isAccepted, onClick } = props

  return (
    <Box flex align='center' gap='x8' justify='space-between'>
      <Box flex align='center' gap='x12'>
        <FileIcon />
        <Body1>{title}</Body1>
      </Box>
      {isAccepted ? (
        <Body2 color='text-success-high-em' whiteSpace='nowrap'>
          {t('Sign up.Accepted')}
        </Body2>
      ) : (
        <Link onClick={onClick}>
          <Body2 whiteSpace='nowrap'>{t('Sign up.Read & Accept')}</Body2>
        </Link>
      )}
    </Box>
  )
}

interface DocumentCheckBoxProps {
  documents: LegalDocumentDto[]
}

export const DocumentCheckBoxGroup: React.FC<DocumentCheckBoxProps> = (props) => {
  const { documents } = props

  const { t } = useTranslation()

  return (
    <React.Fragment>
      <span className='text'>
        {t('Sign up.I confirm that I have read, consent and agree to the')}
      </span>
      {documents
        .map<React.ReactNode>((x) => renderDocumentLink(x))
        .reduce((prev, curr) => [prev, ', ', curr])}{' '}
      <span className='text'>*</span>
    </React.Fragment>
  )
}

export const DocumentCheckBox: React.FC<DocumentCheckBoxProps> = (props) => {
  const { documents = [] } = props
  const [document] = documents

  const { t } = useTranslation()

  return (
    <React.Fragment>
      {document?.url && <span className='text'>{t('I agree with')}</span>}
      {renderDocumentLink(document)} <span className='text'>*</span>
    </React.Fragment>
  )
}

const renderDocumentLink = (document: LegalDocumentDto) => {
  const { url, title } = document

  if (!url) {
    return <span key={document.id}> {title}</span>
  }

  return (
    <Link key={document.id} title={title || undefined} href={url} size='lg'>
      {' '}
      {title}
    </Link>
  )
}
